/**
 * Finance page routes: Treasury at Aithora and Facility scope (src/aithora/v3/finance/money.ts)
 * and Billing (src/aithora/reimbursement.ts under /api/financial/reimbursement, and
 * /api/aithora/usage), and the board's row actions (src/aithora/v3/finance/money-actions.ts).
 * Every route here is served.
 */
import { route } from './route';
import { AITHORA_PREFIX } from './prefix';
import type {
  BillingBoard,
  BillingPractitioner,
  ClaimsExport,
  ClearinghouseConfig,
  CodeCatalog,
  FinanceAithora,
  FinanceFacility,
  GenerateClaimsResult,
  OkWrite,
  ReimbursementSummary,
  SarList,
  SubmitClaimsResult,
} from './finance-types';

const A = AITHORA_PREFIX;
const R = '/api/financial/reimbursement';

/** GET /finance/aithora: network MRR, ARR, NRR, cash and runway, receivables. Operator only. */
export const financeAithora = route<FinanceAithora>()('GET', `${A}/finance/aithora`, { honest: true });
/** GET /finance/facility: one building's receivables, claims, trust and SAR queue. */
export const financeFacility = route<FinanceFacility, { facility?: string; period?: string }>()('GET', `${A}/finance/facility`, { honest: true });
/** The board pack's CSV path (GET /finance/board-pack?facility=&format=csv): every figure stamped. */
export const BOARD_PACK_PATH = `${A}/finance/board-pack`;
/** GET /finance/sar: the suspicious activity reports themselves. */
export const financeSar = route<SarList, { facility?: string }>()('GET', `${A}/finance/sar`, { honest: true });
/** POST /finance/sar/:id/dismiss {reason} */
export const financeSarDismiss = route<{ ok: true; id: string; status: 'dismissed'; real: true }, void, { reason: string }>()('POST', `${A}/finance/sar/:id/dismiss`);
/** POST /finance/sar/:id/file {filingReference} */
export const financeSarFile = route<{ ok: true; id: string; status: 'filed'; real: true }, void, { filingReference: string }>()('POST', `${A}/finance/sar/:id/file`);
/** POST /finance/ar/:id/chase: chase an outstanding claim receivable. */
export const financeArChase = route<{ ok: true; chaseId: string; recipients: number; real: true }, void, { note?: string }>()('POST', `${A}/finance/ar/:id/chase`);
/** POST /finance/trust/reconciliations: reconcile the trust ledger against the bank statement. */
export const financeTrustReconcile = route<OkWrite & { varianceCents: number }, void, { reconciledThrough: string; statementBalanceCents: number; confirm?: true }>()(
  'POST', `${A}/finance/trust/reconciliations`);

// ── Billing ─────────────────────────────────────────────────────────────────
type Org = { organizationId?: string; period?: string };
/** GET /api/financial/reimbursement/summary: eligible claims and dollars by code. */
export const reimbursementSummary = route<ReimbursementSummary, Org>()('GET', `${R}/summary`);
/** GET /api/financial/reimbursement/claims/export: the period's eligible claims (JSON). */
export const reimbursementClaims = route<ClaimsExport, Org>()('GET', `${R}/claims/export`);
/** POST /api/financial/reimbursement/claims/generate: materialise the period's claims. */
export const reimbursementGenerate = route<GenerateClaimsResult, Org, Record<string, never>>()('POST', `${R}/claims/generate`);
/** GET /api/financial/reimbursement/practitioners */
export const reimbursementPractitioners = route<{ practitioners: BillingPractitioner[]; qhpCredentials: string[] }, { organizationId?: string }>()('GET', `${R}/practitioners`);
/** POST /api/financial/reimbursement/practitioners: register a billing practitioner. */
export const reimbursementAddPractitioner = route<
  { practitioner: BillingPractitioner },
  void,
  { organizationId?: string; npi: string; firstName?: string; lastName?: string; credential: string; ptan?: string; medicareEnrolled?: boolean }
>()('POST', `${R}/practitioners`);
/** GET /api/financial/reimbursement/codes: the billable-code catalog with AI-capture classes. */
export const reimbursementCodes = route<CodeCatalog, { organizationId?: string }>()('GET', `${R}/codes`);
/** GET /api/financial/reimbursement/clearinghouse/config */
export const clearinghouseConfig = route<ClearinghouseConfig, { organizationId?: string }>()('GET', `${R}/clearinghouse/config`);
/** One state's Medicaid coverage for a code (state-medicaid-rates.ts): FFS with a verified rate, managed care, or unverified. */
export interface StateMedicaidRate {
  state: string;
  cpt: string;
  status: 'ffs' | 'managed_care' | 'unverified';
  cents?: number;
  verified: boolean;
  source?: string;
  note?: string;
}
/** GET /api/financial/reimbursement/state-rates?state=&cpt=: Aithora's billing desk (restored from production). */
export const stateMedicaidRate = route<{ rate: StateMedicaidRate }, { state: string; cpt: string }>()('GET', `${R}/state-rates`);
/** PUT /api/financial/reimbursement/clearinghouse/config */
export const clearinghouseSave = route<{ ok: true }, void, {
  organizationId: string; provider: 'stedi' | 'claimmd'; environment: 'sandbox' | 'production'; enabled: boolean;
  /** The 837P's billing identity, stored on the organization (blank fields leave it as it is). */
  billingIdentity?: { billingNpi?: string; taxId?: string; payerId?: string; payerName?: string };
}>()(
  'PUT', `${R}/clearinghouse/config`);
/** POST /api/financial/reimbursement/claims/submit: send the signed, ready claims. */
export const clearinghouseSubmit = route<SubmitClaimsResult, Org, Record<string, never>>()('POST', `${R}/claims/submit`);

// ── The board's bill and row actions ────────────────────────────────────────

/** GET /finance/billing: the bill (usage, outcome fee, how it is built) and what would be denied today. */
export const financeBilling = route<BillingBoard, { facility?: string; period?: string }>()('GET', `${A}/finance/billing`, { honest: true });
/** One claim on a building's read-only billing view; the code is null while masked. */
export interface FacilityBillingClaim {
  id: string;
  residentId: string;
  resident: string;
  cpt: string | null;
  description: string;
  period: string;
  minutes: number;
  deviceDays: number;
  amount: number;
  status: string;
}
export interface FacilityBillingClaims {
  real: true;
  /** True for a Facility Admin: they see, they never submit (Andrew, 2026-09-30). */
  readOnly: boolean;
  period: string;
  organizationId: string;
  masked: boolean;
  count: number;
  totalAmount: number;
  /** Built, signed or sent, not yet paid or denied. */
  expectedCents: number;
  paidCents: number;
  deniedCents: number;
  claims: FacilityBillingClaim[];
}
/** GET /finance/billing/claims?facility=&period=: the building's claims, read-only (a Facility Admin's own). */
export const financeBillingClaims = route<FacilityBillingClaims, { facility?: string; period?: string }>()('GET', `${A}/finance/billing/claims`);
// ── Facility invoices through Stripe Invoicing (Andrew, 2026-09-30) ─────────

export interface FacilityInvoice {
  id: string;
  organizationId: string;
  facilityName?: string | null;
  period: string;
  status: 'draft' | 'open' | 'paid' | 'payment_failed' | 'void' | 'uncollectible';
  occupiedBeds: number | null;
  rateCents: number | null;
  amountCents: number;
  contractYear: number | null;
  /** ACH first, card as the fallback. */
  paymentMethods: string[];
  hostedInvoiceUrl: string | null;
  createdAt: string | null;
  sentAt: string | null;
  paidAt: string | null;
  error: string | null;
}
/** GET /finance/invoices?facility=: real:false with the reason until STRIPE_SECRET_KEY is set. */
export const financeInvoices = route<{ real: boolean; configured: boolean; reason: string | null; invoices: FacilityInvoice[] }, { facility?: string }>()(
  'GET', `${A}/finance/invoices`);
export interface InvoicePreview {
  real: true;
  configured: boolean;
  organizationId: string;
  period: string;
  billable: boolean;
  reason: string | null;
  occupiedBeds: number | null;
  rateCents: number | null;
  contractYear: number | null;
  amountCents: number;
  imputedShare: number | null;
  description: string | null;
}
/** GET /finance/invoices/preview?facility=&period=: what a month's invoice is (per occupied bed). */
export const financeInvoicePreview = route<InvoicePreview, { facility?: string; period: string }>()('GET', `${A}/finance/invoices/preview`);
/** POST /finance/invoices/issue {facility, period}: Aithora operators only; 503 until Stripe is connected. */
export const financeInvoiceIssue = route<{ real: true; created: boolean; invoice: FacilityInvoice }, void, { facility: string; period: string }>()(
  'POST', `${A}/finance/invoices/issue`);
/** POST /finance/actions/:id: act on an Act now signal, audited. 404 when the signal is no longer raised. */
export const financeAct = route<OkWrite, void, { action: string; facility?: string }>()('POST', `${A}/finance/actions/:id`);
/** POST /finance/invoices/:id/chase: operators only. 409 (real:false) until Aithora issues invoices from the app. */
export const financeInvoiceChase = route<OkWrite, void, Record<string, never>>()('POST', `${A}/finance/invoices/:id/chase`);
/** POST /finance/denials/:id/rework: queue a denial reason's claims for rework (capture_actions 'rework'). */
export const financeDenialRework = route<OkWrite, void, { facility?: string }>()('POST', `${A}/finance/denials/:id/rework`);
/** POST /finance/trust/tasks: a trust-rule fix, a spend-down plan or a charge review. */
export const financeTrustTask = route<OkWrite, void, { kind: 'rule' | 'spend_down' | 'charge'; id: string; facility?: string }>()('POST', `${A}/finance/trust/tasks`);
/** POST /finance/sar/:id/actions: review, escalate or open a DRAFT APS referral. */
export const financeSarAction = route<OkWrite, void, { action: 'review' | 'escalate' | 'file_aps' }>()('POST', `${A}/finance/sar/:id/actions`);

export const financeApi = {
  financeAithora,
  financeFacility,
  financeSar,
  financeSarDismiss,
  financeSarFile,
  financeArChase,
  financeTrustReconcile,
  reimbursementSummary,
  reimbursementClaims,
  reimbursementGenerate,
  reimbursementPractitioners,
  reimbursementAddPractitioner,
  reimbursementCodes,
  clearinghouseConfig,
  clearinghouseSave,
  clearinghouseSubmit,
  financeBilling,
  financeAct,
  financeInvoiceChase,
  financeDenialRework,
  financeTrustTask,
  financeSarAction,
} as const;
